import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar, CreditCard, Clock, AlertCircle, FileText, Euro, DollarSign, Banknote, History, RefreshCw, Edit, Trash2, MoreHorizontal, Loader2, Download } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { useCurrencySettings } from '@/hooks/useCurrencySettings';
import { useTranslation } from 'react-i18next';
import { useDojoPaymentMethods } from '@/hooks/useDojoPaymentMethods';
import InvoiceGenerator from './InvoiceGenerator';
import PaymentDateEditDialog from './PaymentDateEditDialog';
import PaymentStatusDialog from './PaymentStatusDialog';
import StudentPaymentDialog from './StudentPaymentDialog';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import PaymentHeader from './payments/PaymentHeader';

interface PaymentInstruction {
  id: string;
  category_name: string;
  subscription_type: string;
  payment_plan: string;
  total_amount: number;
  amount_paid: number;
  installments_paid: number;
  total_installments: number;
  status: 'pending' | 'partial' | 'complete';
  next_due_date?: string;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
}

interface PaymentRecord {
  id: string;
  amount: number;
  due_date: string;
  paid_date?: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled' | 'partial';
  payment_type: 'monthly' | 'promotion' | 'equipment' | 'registration' | 'trial' | 'other';
  description?: string;
  payment_method?: string;
  created_at: string;
  notes?: string;
  category_name?: string;
  subscription_type?: string;
  total_amount?: number;
  amount_paid?: number;
  installments_paid?: number;
  total_installments?: number;
  next_due_date?: string;
  start_date?: string;
  end_date?: string;
  invoiceGenerated?: boolean; // Assuming this might be a field relevant to invoices
}

interface StudentPaymentTrackerProps {
  hideInvoices?: boolean;
}

const StudentPaymentTracker: React.FC<StudentPaymentTrackerProps> = ({ hideInvoices = false }) => {
  useUiLanguage();
  // All state declarations at the top level - never conditional
  const [paymentRecords, setPaymentRecords] = useState<PaymentRecord[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [editingPayment, setEditingPayment] = useState<PaymentRecord | null>(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [paymentStatusDialog, setPaymentStatusDialog] = useState<PaymentRecord | null>(null);
  const [newPaymentStatus, setNewPaymentStatus] = useState<'pending' | 'partial' | 'complete'>('pending');
  const [amountPaid, setAmountPaid] = useState(0);
  const [paymentDate, setPaymentDate] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPaymentAmount, setSelectedPaymentAmount] = useState(0);
  const [showExport, setShowExport] = useState(false); // Added state for export dialog
  const [downloadingInvoice, setDownloadingInvoice] = useState<string | null>(null); // State for downloading invoice

  // All hook calls at the top level - never conditional
  const { profile } = useAuth();
  const { defaultCurrency, formatCurrency } = useCurrencySettings();
  const { t } = useTranslation('payments');
  const { availablePaymentMethods, isLoading: paymentMethodsLoading } = useDojoPaymentMethods();

  // Memoized calculations
  const hasPaymentData = useMemo(() => paymentRecords.length > 0, [paymentRecords]);
  const firstPayment = useMemo(() => payments.length > 0 ? payments[0] : null, [payments]);
  const currencySymbol = useMemo(() => getCurrencySymbol(currency), [currency]);

  const fetchPaymentData = useCallback(async () => {
    if (!profile?.id || !profile?.dojo_id) return;

    setIsRefreshing(true);
    setError(null);

    try {
      console.log('📊 Fetching payment data for student:', profile.id, 'in dojo:', profile.dojo_id);

      // Fetch payment instructions without join to avoid schema issues
      const { data: paymentData, error } = await supabase
        .from('payment_instructions')
        .select('*')
        .eq('student_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error fetching payment instructions:', error);
        setError(uiText("Failed to load payment data"));
        return;
      }

      console.log('✅ Raw payment data:', paymentData);

      // Convert to payment records format for compatibility
      const mappedPayments = (paymentData || []).map(instruction => ({
        id: instruction.id,
        amount: instruction.total_amount || 0,
        due_date: instruction.next_due_date || instruction.created_at,
        paid_date: instruction.payment_date,
        status: instruction.status as 'pending' | 'paid' | 'overdue' | 'cancelled' | 'partial',
        payment_type: 'monthly' as const,
        description: instruction.category_name || 'Payment',
        created_at: instruction.created_at,
        category_name: instruction.category_name,
        subscription_type: instruction.subscription_type,
        total_amount: instruction.total_amount,
        amount_paid: instruction.amount_paid,
        installments_paid: instruction.installments_paid,
        total_installments: instruction.total_installments,
        next_due_date: instruction.next_due_date,
        start_date: instruction.start_date,
        end_date: instruction.end_date,
        // Assuming invoiceGenerated would be a field in your payment_instructions table or derived
        invoiceGenerated: !!instruction.id // Placeholder: replace with actual logic if available
      }));

      console.log('📋 Formatted payments:', mappedPayments);

      setPaymentRecords(mappedPayments);
      setPayments(paymentData || []);
      setCurrency(defaultCurrency);
    } catch (error) {
      console.error('Error in fetchPaymentData:', error);
      setError(uiText("Failed to load payment data"));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [profile?.id, profile?.dojo_id, defaultCurrency]);

  useEffect(() => {
    if (profile?.id) {
      setIsLoading(true);
      fetchPaymentData();
    }
  }, [fetchPaymentData]);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'complete':
      case 'paid':
        return 'default';
      case 'partial':
        return 'secondary';
      case 'pending':
        return 'outline';
      case 'overdue':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: t("payments:status.pending", 'Pending'),
      partial: t("payments:status.partial", 'Partial'),
      complete: t("payments:status.paid", 'Paid'),
      paid: t("payments:status.paid", 'Paid'),
      overdue: t("payments:status.overdue", 'Overdue'),
      cancelled: t("payments:status.cancelled", 'Cancelled')
    };
    return statusMap[status] || status;
  };

  const getPaymentTypeColor = (paymentType: string) => {
    switch (paymentType) {
      case 'monthly': return 'bg-blue-100 text-blue-700';
      case 'promotion': return 'bg-green-100 text-green-700';
      case 'equipment': return 'bg-orange-100 text-orange-700';
      case 'registration': return 'bg-purple-100 text-purple-700';
      case 'trial': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPaymentIcon = (paymentType: string) => {
    switch (paymentType) {
      case 'monthly': return <DollarSign className="h-4 w-4" />;
      case 'promotion': return <Banknote className="h-4 w-4" />;
      case 'equipment': return <Euro className="h-4 w-4" />;
      case 'registration': return <FileText className="h-4 w-4" />;
      case 'trial': return <AlertCircle className="h-4 w-4" />;
      default: return <CreditCard className="h-4 w-4" />;
    }
  };

  const formatPaymentType = (paymentType: string) => {
    return paymentType.charAt(0).toUpperCase() + paymentType.slice(1);
  };

  const handleEditPayment = (payment: PaymentRecord) => {
    setEditingPayment(payment);
    setEditStartDate(payment.due_date);
    setEditEndDate(payment.paid_date || payment.due_date);
  };

  const handleSaveEdit = async () => {
    if (!editingPayment) return;

    try {
      const { error } = await supabase
        .from('payment_instructions')
        .update({
          next_due_date: editStartDate,
          payment_date: editEndDate,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingPayment.id);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Payment dates updated successfully")
      });

      setEditingPayment(null);
      fetchPaymentData(); // Refresh data
    } catch (error) {
      console.error('Error updating payment:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update payment dates"),
        variant: "destructive"
      });
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm(uiText("Are you sure you want to delete this payment record?"))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('payment_instructions')
        .delete()
        .eq('id', paymentId);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Payment record deleted successfully")
      });

      fetchPaymentData(); // Refresh data
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to delete payment record"),
        variant: "destructive"
      });
    }
  };

  const handleUpdatePaymentStatus = async () => {
    if (!paymentStatusDialog) return;

    try {
      const { error } = await supabase
        .from('payment_instructions')
        .update({
          status: newPaymentStatus,
          amount_paid: amountPaid,
          payment_date: paymentDate,
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentStatusDialog.id);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Payment status updated successfully")
      });

      setPaymentStatusDialog(null);
      fetchPaymentData(); // Refresh data
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update payment status"),
        variant: "destructive"
      });
    }
  };

  const openPaymentStatusDialog = (payment: PaymentRecord) => {
    setPaymentStatusDialog(payment);
    setNewPaymentStatus(payment.status === 'paid' ? 'complete' : payment.status === 'partial' ? 'partial' : 'pending');
    setAmountPaid(payment.amount);
    setPaymentDate(payment.paid_date || new Date().toISOString().split('T')[0]);
  };

  // Placeholder for download invoice functionality
  const downloadInvoice = async (paymentId: string) => {
    setDownloadingInvoice(paymentId);
    // Implement actual invoice download logic here
    console.log(`Downloading invoice for payment ID: ${paymentId}`);
    // Simulate download delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    setDownloadingInvoice(null);
    toast({
      title: uiText("Invoice Download"),
      description: uiText("Invoice for payment {{value0}} is being downloaded (simulated).", { value0: paymentId }),
    });
  };


  // Early returns for error and loading states - but hooks are already called above
  if (error) {
    return (
      <Card className="border-dashed border-red-200">
        <CardHeader className="text-center pb-6">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <CardTitle className="text-xl text-red-700">{uiText("Error")}</CardTitle>
          <CardDescription className="text-base text-red-600">
            {error}
          </CardDescription>
          <Button
            onClick={fetchPaymentData}
            variant="outline"
            className="mt-4 mx-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {uiText("Try Again")}</Button>
        </CardHeader>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Payment History Header - Hidden when hideInvoices is true */}
        {!hideInvoices && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-lg shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">{uiText("Payment History")}</h2>
                <p className="text-blue-100 text-sm sm:text-base">
                  {uiText("Manage your training payments and subscriptions")}</p>
              </div>
              <div className="flex items-center gap-2 text-blue-100">
                <span className="text-xs sm:text-sm">{uiText("Currency:")}</span>
                <span className="font-semibold">{currencySymbol}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.location.reload()}
                  className="text-blue-100 hover:text-white hover:bg-white/20"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-blue-100 hover:text-white hover:bg-white/20"
                >
                  <FileText className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-2">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="text-muted-foreground">{t("payments:loading", 'Loading payment history...')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!hasPaymentData) {
    return (
      <Card className="border-dashed">
        <CardHeader className="text-center pb-6">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="h-8 w-8 text-muted-foreground" />
          </div>
          <CardTitle className="text-xl">{t("payments:noInstructions.title", 'No Payment History')}</CardTitle>
          <CardDescription className="text-base">
            {t("payments:noInstructions.description", 'You have no payment records yet. Your payment history will appear here once you make your first payment.')}</CardDescription>
          <p className="text-sm text-gray-400 mt-2">{uiText("Contact your instructor if you believe this is an error.")}</p>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      {/* Payment History Header - Hidden when hideInvoices is true */}
      {!hideInvoices && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-lg shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold">{uiText("Payment History")}</h2>
              <p className="text-blue-100 text-sm sm:text-base">
                {uiText("Manage your training payments and subscriptions")}</p>
            </div>
            <div className="flex items-center gap-2 text-blue-100">
              <span className="text-xs sm:text-sm">{uiText("Currency:")}</span>
              <span className="font-semibold">{currencySymbol}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchPaymentData}
                disabled={isRefreshing}
                className="text-blue-100 hover:text-white hover:bg-white/20 p-1.5 sm:p-2"
              >
                <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowExport(true)}
                className="text-blue-100 hover:text-white hover:bg-white/20 p-1.5 sm:p-2"
              >
                <FileText className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}
      <CardContent className="p-3 sm:p-4 md:p-6">
        <div className="space-y-4">
          {/* Subscription Section */}
          {firstPayment && (
            <Card className="border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
              <CardContent className="p-3 sm:p-4">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-gray-100 break-words">
                        {firstPayment.category_name || uiText("Subscription")}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1">
                        {firstPayment.subscription_type === '1year' ? uiText("Adults • 1 Year") :
                         firstPayment.subscription_type === '6months' ? uiText("Adults • 6 Months") :
                         firstPayment.subscription_type === '3months' ? uiText("Adults • 3 Months") :
                         firstPayment.subscription_type === 'monthly' ? uiText("Adults • Monthly") : uiText("Subscription")}
                      </p>
                      <Badge variant={getStatusBadgeVariant(firstPayment.status)} className="mt-2 text-xs">
                        {getStatusBadgeText(firstPayment.status)}
                      </Badge>
                    </div>
                    <div className="text-right sm:text-right">
                      <div className="font-bold text-lg sm:text-xl text-gray-900 dark:text-gray-100">
                        {formatCurrency(firstPayment.total_amount / 100)}
                      </div>
                      {firstPayment.status === 'partial' && (
                        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1">
                          {uiText("Paid:")}{' '}{formatCurrency((firstPayment.amount_paid || 0) / 100)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Progress */}
                  {firstPayment.total_installments > 1 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs sm:text-sm text-gray-700 dark:text-gray-200">
                        <span>{uiText("Payment Progress")}</span>
                        <span>{firstPayment.installments_paid || 0} {' '}{uiText("of")}{' '}{firstPayment.total_installments}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${((firstPayment.installments_paid || 0) / firstPayment.total_installments) * 100}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Payment Button */}
                  {firstPayment.status !== 'complete' && (
                    <Button
                      onClick={() => {
                        setSelectedPaymentAmount(firstPayment.total_amount - (firstPayment.amount_paid || 0));
                        setShowPaymentDialog(true);
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white responsive-button"
                    >
                      <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      <span className="text-xs sm:text-sm">
                        {uiText("Make Payment (")}{formatCurrency((firstPayment.total_amount - (firstPayment.amount_paid || 0)) / 100)})
                      </span>
                    </Button>
                  )}

                  {/* Invoice Section - Only show if hideInvoices is false */}
                  {!hideInvoices && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{uiText("Invoice")}</h4>
                          <p className="text-xs text-gray-500">
                            {firstPayment.invoiceGenerated ? uiText("Invoice available") : uiText("No invoice generated")}
                          </p>
                        </div>
                        {firstPayment.invoiceGenerated ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadInvoice(firstPayment.id)}
                            disabled={downloadingInvoice === firstPayment.id}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            {downloadingInvoice === firstPayment.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            {uiText("Download")}</Button>
                        ) : (
                          <div className="flex items-center text-gray-400">
                            <FileText className="h-4 w-4 mr-1" />
                            <span className="text-xs">{uiText("Not available")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No subscription message */}
          {!firstPayment && (
            <Card className="border-dashed">
              <CardHeader className="text-center pb-6">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="h-8 w-8 text-muted-foreground" />
                </div>
                <CardTitle className="text-xl">{uiText("No Active Subscription")}</CardTitle>
                <CardDescription className="text-base">
                  {uiText("You don't have an active subscription yet. Contact your instructor to get started.")}</CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>

        {/* Payment Dialog with Methods from Admin */}
        <StudentPaymentDialog
          open={showPaymentDialog}
          setOpen={setShowPaymentDialog}
          paymentAmount={selectedPaymentAmount}
          studentName={profile?.first_name + ' ' + profile?.last_name}
        />

        {/* Invoice Export Dialog */}
        <Dialog open={showExport} onOpenChange={setShowExport}>
          <DialogContent className="max-w-md mx-4 rounded-lg">
            <DialogHeader>
              <DialogTitle className="text-lg">{uiText("Export Invoice")}</DialogTitle>
              <DialogDescription className="text-sm">
                {uiText("Generate an invoice for your payments")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {firstPayment ? (
                <InvoiceGenerator
                  paymentRecord={{
                    id: firstPayment.id,
                    studentName: profile?.first_name + ' ' + profile?.last_name || 'Student',
                    email: profile?.email || '',
                    category: firstPayment.category_name || 'Subscription',
                    subscriptionType: firstPayment.subscription_type,
                    paymentPlan: firstPayment.payment_plan || '1',
                    totalAmount: firstPayment.total_amount,
                    amountPaid: firstPayment.amount_paid || 0,
                    installmentsPaid: firstPayment.installments_paid || 0,
                    totalInstallments: firstPayment.total_installments || 1,
                    status: firstPayment.status,
                    nextDueDate: firstPayment.next_due_date,
                    startDate: firstPayment.start_date,
                    endDate: firstPayment.end_date
                  }}
                  onClose={() => setShowExport(false)}
                  onInvoiceGenerated={() => {
                    console.log('Invoice generated for student');
                  }}
                />
              ) : (
                <div className="text-center py-6">
                  <FileText className="h-8 w-8 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-500 text-sm">
                    {uiText("No payment data available for invoice generation.")}</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default StudentPaymentTracker;